import React, { useEffect, useState } from 'react';
import { Mail, Phone, Send } from 'lucide-react';
import Input from '../components/elements/Input';
import Button from '../components/elements/Button';
import Info from '../components/elements/Info';
import { sendContactUs } from '../utils/ContactUsApi';
import { isValidEmail } from '../utils/globalUtils';
import Header from '../components/Header';

export default function ContactUsPage() {
    const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
    const [loggedInEmail] = useState(sessionStorage.getItem('email') || '');
    const [loggedInFirstName] = useState(sessionStorage.getItem('firstName') || '');
    const [loggedInLastName] = useState(sessionStorage.getItem('lastName') || '');
    const loggedInFullName = `${loggedInFirstName} ${loggedInLastName}`.trim();
    const [form, setForm] = useState({ name: loggedInFullName, email: loggedInEmail, message: '' });
    const [formErrors, setFormErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    useEffect(() => {
        document.title = "Contact Us | Eternal Locker™";
    }, []);

    useEffect(() => {
        setFormErrors({});
    }, [form]);

    const validateForm = () => {
        const errors = {};
        if (form.name.trim() === '') {
            errors.name = 'Name is required';
        }
        if (form.email.trim() === '') {
            errors.email = 'Email is required';
        } else if (!isValidEmail(form.email)) {
            errors.email = 'Invalid email format';
        }
        if (form.message.trim() === '') {
            errors.message = 'Message is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }

    const handleSubmit = async e => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        setLoading(true);
        setSubmitError(null);
        try {
            await sendContactUs(form);
            setSubmitted(true);
        } catch (err) {
            setSubmitError(err.message || 'Failed to send message. Please try again.');
        }
        setLoading(false);
    };

    return (
        <>
            <Header type="small" showAccountDropdown={isLoggedIn} />
            <div className="flex justify-center w-full font-sans">
                <div className="w-full max-w-2xl p-8 space-y-6 bg-background-light shadow-2xl rounded-xl border border-accent-main">
                    <h1 className="text-3xl font-bold text-center text-accent-main">Contact Us</h1>
                    <div className="flex flex-col sm:flex-row gap-8 p-4 bg-primary-light border border-accent-main rounded-xl shadow">
                    {submitted && (
                        <div className="text-accent-main text-lg font-semibold flex items-center justify-center gap-2">
                            <Send className="w-5 h-5" />Thank you! We'll be in touch soon.
                        </div>
                    )}

                    {!submitted && (
                        <div>
                            <p className="mb-6 text-primary-dark">Have questions, feedback, or need help? Fill out the form below and our team will get back to you soon.</p>
                            <div className="mb-4 sm:flex flex-wrap items-center gap-4">
                                <div className="flex items-center gap-2">
                                <Phone className="w-5 h-5 text-accent-main" />
                                <span className="text-primary-dark">+91 7972604679</span>
                                </div>
                                <div className="flex items-center gap-2">
                                <Mail className="w-5 h-5 text-accent-main" />
                                <span className="text-primary-dark">{process.env.REACT_APP_SUPPORT_EMAIL || 'support@eternallocker.com'}</span>
                                </div>
                            </div>
                            <form className="space-y-4">
                                {submitError && (
                                    <Info type="error">{submitError}</Info>
                                )}
                                <Input
                                    label="Name"
                                    value={form.name}
                                    onChange={e => setForm({ ...form, name: e.target.value })}
                                    placeholder="Your Name"
                                    required
                                    disabled={!!loggedInFullName}
                                    error={formErrors.name}
                                />
                                <Input
                                    label="Email"
                                    type="email"
                                    value={form.email}
                                    onChange={e => setForm({ ...form, email: e.target.value })}
                                    placeholder="you@email.com"
                                    required
                                    disabled={!!loggedInEmail}
                                    error={formErrors.email}
                                />
                                <Input
                                    label="Message"
                                    type="textarea"
                                    value={form.message}
                                    onChange={e => setForm({ ...form, message: e.target.value })}
                                    placeholder="How can we help you?"
                                    rows={5}
                                    required
                                    error={formErrors.message}
                                />
                                <Button type="primary" onClick={handleSubmit} className="ml-auto" loading={loading} disabled={loading}>
                                    Send Message
                                </Button>
                            </form>
                        </div>
                    )}
                    </div>
                </div>
            </div>
        </>
    );
}

